// =============================================================================
// TrackerFilters.jsx
//
// Barra de filtros horizontal sempre visível acima da tabela.
//
// Filtros disponíveis:
//   - Date Range:  De / Até  (filtra actividades cujo período se sobrepõe)
//   - Technology:  multi-select (2G, 3G, 4G, 5G)
//   - Team:        multi-select (derivado das entries)
//   - Owner:       multi-select (derivado das entries)
//
// Props:
//   entries    → array completo (para derivar opções dos dropdowns)
//   filters    → { dateFrom, dateTo, technologies[], teams[], owners[] }
//   onChange   → callback(newFilters)
//   onClear    → callback() — limpa todos os filtros
//   filtered   → número de resultados após filtragem (para mostrar no badge)
// =============================================================================

import React from 'react';
import {
  Box, Paper, TextField, MenuItem, Chip, Typography, Button,
  InputAdornment, Select, FormControl, InputLabel, OutlinedInput,
  Checkbox, ListItemText, Tooltip, Divider,
} from '@mui/material';
import FilterListIcon        from '@mui/icons-material/FilterList';
import ClearIcon             from '@mui/icons-material/Clear';
import { DatePicker }        from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment }     from '@mui/x-date-pickers/AdapterMoment';
import moment                from 'moment';

// Altura máx do menu dos multi-selects
const MENU_PROPS = { PaperProps: { style: { maxHeight: 300 } } };

// ── Helper: renderValue para multi-select ────────────────────────────────────
const renderChips = (selected, placeholder) =>
  selected.length === 0
    ? <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.82rem' }}>{placeholder}</Typography>
    : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((v) => (
          <Chip key={v} label={v} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
        ))}
      </Box>;

// ── Componente ────────────────────────────────────────────────────────────────
// options → { techs, teams, owners, features, vendors, sides }, já calculado
// pelo TrackerPage.jsx (deriveFilterOptions) em cascata com os restantes
// filtros activos — cada dropdown mostra só o que ainda é possível escolher
// dado o que já está seleccionado nos outros (faceted search).
const TrackerFilters = ({ entries, filters, onChange, onClear, filtered, options }) => {

  const hasFilters =
    filters.dateFrom || filters.dateTo ||
    filters.sides.length > 0 ||
    filters.technologies.length > 0 ||
    filters.teams.length > 0 ||
    filters.owners.length > 0 ||
    filters.features.length > 0 ||
    filters.vendors.length > 0;

  const totalEntries = entries.length;
  const isFiltered   = hasFilters && filtered < totalEntries;

  // Limites do From/To — intervalo [min, max] de StartDate das atividades
  // que já passam nos restantes filtros activos (options.dateBounds, vindo
  // do TrackerPage.jsx), mesmo princípio de cascata dos outros dropdowns.
  const boundsMinMoment = options.dateBounds?.min ? moment(options.dateBounds.min, 'YYYY-MM-DD') : undefined;
  const boundsMaxMoment = options.dateBounds?.max ? moment(options.dateBounds.max, 'YYYY-MM-DD') : undefined;
  const fromMoment      = filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD') : undefined;
  // "To" nunca pode ser antes do "From" já escolhido, nem antes do mínimo da cascata
  const toMinDate = fromMoment && boundsMinMoment
    ? moment.max(fromMoment, boundsMinMoment)
    : (fromMoment ?? boundsMinMoment);

  // ── Handler genérico para multi-selects ──────────────────────────────────
  const handleMulti = (key) => (e) => {
    const val = typeof e.target.value === 'string'
      ? e.target.value.split(',')
      : e.target.value;
    onChange({ ...filters, [key]: val });
  };

  return (
    <Box sx={{ px: 0, py: 1, mb: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>

        {/* ── Ícone + label ─────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 0.5 }}>
          <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
            Filters
          </Typography>
        </Box>

        {/* ── Date From/To — min/max em cascata com os restantes filtros ── */}
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="From"
            format="YYYY-MM-DD"
            value={filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD') : null}
            onChange={(val) => onChange({ ...filters, dateFrom: val?.isValid() ? val.format('YYYY-MM-DD') : null })}
            minDate={boundsMinMoment}
            maxDate={boundsMaxMoment}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />

          {/* ── Date To ───────────────────────────────────────────────────── */}
          <DatePicker
            label="To"
            format="YYYY-MM-DD"
            value={filters.dateTo ? moment(filters.dateTo, 'YYYY-MM-DD') : null}
            onChange={(val) => onChange({ ...filters, dateTo: val?.isValid() ? val.format('YYYY-MM-DD') : null })}
            minDate={toMinDate}
            maxDate={boundsMaxMoment}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
        </LocalizationProvider>

        {/* ── Side multi-select (East/West/None) ────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Side</InputLabel>
          <Select
            multiple
            value={filters.sides}
            onChange={handleMulti('sides')}
            input={<OutlinedInput notched label="Side" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.sides.map((s) => (
              <MenuItem key={s} value={s} dense>
                <Checkbox checked={filters.sides.includes(s)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={s} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Technology multi-select ───────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Technology</InputLabel>
          <Select
            multiple
            value={filters.technologies}
            onChange={handleMulti('technologies')}
            input={<OutlinedInput notched label="Technology" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.techs.map((t) => (
              <MenuItem key={t} value={t} dense>
                <Checkbox checked={filters.technologies.includes(t)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={t} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Team multi-select ─────────────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Team</InputLabel>
          <Select
            multiple
            value={filters.teams}
            onChange={handleMulti('teams')}
            input={<OutlinedInput notched label="Team" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.teams.map((t) => (
              <MenuItem key={t} value={t} dense>
                <Checkbox checked={filters.teams.includes(t)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={t} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Owner multi-select ────────────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Owner</InputLabel>
          <Select
            multiple
            value={filters.owners}
            onChange={handleMulti('owners')}
            input={<OutlinedInput notched label="Owner" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.owners.map((o) => (
              <MenuItem key={o} value={o} dense>
                <Checkbox checked={filters.owners.includes(o)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={o} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Type multi-select ───────────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Type</InputLabel>
          <Select
            multiple
            value={filters.features}
            onChange={handleMulti('features')}
            input={<OutlinedInput notched label="Type" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.features.map((f) => (
              <MenuItem key={f} value={f} dense>
                <Checkbox checked={filters.features.includes(f)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={f} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Vendor multi-select ───────────────────────────────────────── */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink sx={{ fontSize: '0.82rem' }}>Vendor</InputLabel>
          <Select
            multiple
            value={filters.vendors}
            onChange={handleMulti('vendors')}
            input={<OutlinedInput notched label="Vendor" />}
            renderValue={(sel) => renderChips(sel, 'All')}
            MenuProps={MENU_PROPS}
            displayEmpty
          >
            {options.vendors.map((v) => (
              <MenuItem key={v} value={v} dense>
                <Checkbox checked={filters.vendors.includes(v)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={v} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Spacer + resultado + clear ────────────────────────────────── */}
        <Box sx={{ flex: 1 }} />

        {isFiltered && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            <strong>{filtered}</strong> of {totalEntries}
          </Typography>
        )}

        {/* Clear sempre visível — desativado se não há filtros activos */}
        <Tooltip title={hasFilters ? 'Clear all filters' : 'No active filters'}>
          <span>
            <Button
              size="small"
              variant={hasFilters ? 'contained' : 'outlined'}
              color={hasFilters ? 'error' : 'inherit'}
              startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
              onClick={onClear}
              disabled={!hasFilters}
              disableElevation
              sx={{
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                minWidth: 110,
                ...(hasFilters ? {} : { color: 'text.disabled', borderColor: 'divider' }),
              }}
            >
              Clear Filters
            </Button>
          </span>
        </Tooltip>

    </Box>
  );
};

export default TrackerFilters;